import { useMemo, useRef, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { FiPackage, FiCalendar, FiUsers } from "react-icons/fi";
import { CheckCircle2, X } from "lucide-react";
import { format, addDays, differenceInDays, isAfter, isBefore, startOfDay } from "date-fns";
import { getReleaseStatusColor } from "@/lib/colorsMap";
import { cn } from "@/lib/utils";

export function ProjectGantt({ isLoading, project, filterByAssignee, filterBy, sortBy, user, onShowReleaseDetails }) {
    const timelineRef = useRef(null);
    
    // Beta overlay state
    const [showBetaOverlay, setShowBetaOverlay] = useState(() => {
        const dismissed = localStorage.getItem('gantt-beta-dismissed');
        return dismissed !== 'true';
    });
    
    // Hover state for coordinated highlighting
    const [hoveredReleaseId, setHoveredReleaseId] = useState(null);

    const handleDismissBeta = () => {
        setShowBetaOverlay(false);
        localStorage.setItem('gantt-beta-dismissed', 'true');
    };
    // Calculate releases with proper status using useMemo for consistency
    const releases = useMemo(() => {
        if (!project?.releases) return [];

        const calculateReleaseStatus = (release) => {
            const now = new Date();
            
            // If actualReleaseDate is set, it's shipped
            if (release.actualReleaseDate) {
                return 'shipped';
            }
            
            // If no start date is set, check if target date has passed
            if (!release.startDate) {
                if (release.targetDate) {
                    const targetDate = new Date(release.targetDate);
                    // If target date has passed, it's overdue
                    if (now > targetDate) {
                        return 'overdue';
                    }
                }
                return 'upcoming';
            }
            
            const startDate = new Date(release.startDate);
            
            // If start date is in the future
            if (now < startDate) {
                return 'upcoming';
            }
            
            // If no target date is set, consider it active after start date
            if (!release.targetDate) {
                return now >= startDate ? 'active' : 'upcoming';
            }
            
            const targetDate = new Date(release.targetDate);
            
            // If we're between start and target date
            if (now >= startDate && now <= targetDate) {
                return 'active';
            }
            
            // If we're past the target date
            return 'overdue';
        };

        return project.releases.map(release => ({
            ...release,
            status: calculateReleaseStatus(release)
        }));
    }, [project?.releases]);

    // Calculate timeline bounds
    const timelineBounds = useMemo(() => {
        if (releases.length === 0) {
            const today = new Date();
            return {
                start: addDays(today, -7),
                end: addDays(today, 30)
            };
        }

        const allDates = releases.flatMap(release => {
            const dates = [];
            if (release.startDate) dates.push(new Date(release.startDate));
            if (release.targetDate) dates.push(new Date(release.targetDate));
            if (release.actualReleaseDate) dates.push(new Date(release.actualReleaseDate));
            return dates;
        });

        if (allDates.length === 0) {
            const today = new Date();
            return {
                start: addDays(today, -7),
                end: addDays(today, 30)
            };
        }

        const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

        return {
            start: addDays(minDate, -7),
            end: addDays(maxDate, 7)
        };
    }, [releases]);

    // Calculate timeline days
    const timelineDays = useMemo(() => {
        const days = [];
        let currentDate = startOfDay(timelineBounds.start);
        const endDate = startOfDay(timelineBounds.end);

        while (currentDate <= endDate) {
            days.push(new Date(currentDate));
            currentDate = addDays(currentDate, 1);
        }

        return days;
    }, [timelineBounds]);

    // Scroll to today's position when timeline loads
    useEffect(() => {
        if (timelineRef.current && timelineBounds) {
            const today = new Date();
            const todayOffset = differenceInDays(today, timelineBounds.start);
            const todayPosition = todayOffset * DAY_WIDTH;
            
            // Scroll to today's position, centered in the viewport
            const containerWidth = timelineRef.current.clientWidth;
            const scrollPosition = todayPosition - (containerWidth / 2);
            
            timelineRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
    }, [timelineBounds]);

    // Calculate current week bounds
    const currentWeekBounds = useMemo(() => {
        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week (Saturday)
        
        return { start: startOfWeek, end: endOfWeek };
    }, []);

    // Get tasks for each release
    const getReleaseTasks = (releaseId) => {
        if (!project?.tasks) return [];
        return project.tasks.filter(task => task.release?.id === releaseId);
    };

    // Calculate progress for a release
    const getReleaseProgress = (releaseId) => {
        const tasks = getReleaseTasks(releaseId);
        if (tasks.length === 0) return 0;
        
        const completedTasks = tasks.filter(task => task.completedAt);
        return Math.round((completedTasks.length / tasks.length) * 100);
    };

    if (isLoading || !project) {
        return (
            <div className="space-y-6 relative">
                <Card className="overflow-hidden">
                    <CardContent className="p-0">
                        <div className="flex">
                            {/* Fixed Release Column */}
                            <div className="flex-shrink-0 border-r border-muted">
                                {/* Header */}
                                <div className="h-12 px-4 flex items-center border-b border-muted">
                                    <Skeleton className="h-4 w-16" />
                                </div>
                                
                                {/* Release Rows */}
                                <div>
                                    {[...Array(6)].map((_, i) => (
                                        <div key={i} className="h-16 px-4 flex items-center border-b border-muted/50">
                                            <div className="flex items-center gap-3 w-80">
                                                <Skeleton className="w-4 h-4 rounded" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Skeleton className="h-4 w-32" />
                                                        <Skeleton className="h-5 w-12 rounded-full" />
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <Skeleton className="h-3 w-16" />
                                                        <Skeleton className="h-3 w-3" />
                                                        <Skeleton className="h-3 w-16" />
                                                        <Skeleton className="h-3 w-3" />
                                                        <Skeleton className="h-3 w-12" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Timeline Column */}
                            <div className="flex-1 overflow-hidden">
                                {/* Timeline Header */}
                                <div className="h-12 border-b border-muted relative">
                                    <div className="flex h-full">
                                        {[...Array(20)].map((_, i) => (
                                            <div key={i} className="flex-1 border-r border-muted/30 last:border-r-0">
                                                <div className="h-full flex items-center justify-center">
                                                    <Skeleton className="h-3 w-8" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Timeline Rows */}
                                <div>
                                    {[...Array(6)].map((_, i) => (
                                        <div key={i} className="h-16 relative border-b border-muted/50">
                                            {/* Grid lines */}
                                            <div className="flex h-full">
                                                {[...Array(20)].map((_, j) => (
                                                    <div key={j} className="flex-1 border-r border-muted/30 last:border-r-0" />
                                                ))}
                                            </div>
                                            
                                            {/* Timeline Bar */}
                                            <div className="absolute top-3 left-8">
                                                <Skeleton className="h-10 w-32 rounded-sm" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const filteredReleases = releases.filter(release => {
        // Apply assignee filter if set
        if (filterByAssignee.size > 0) {
            const releaseTasks = getReleaseTasks(release._id);
            const hasAssignedTasks = releaseTasks.some(task => 
                task.assignee && filterByAssignee.has(task.assignee.id)
            );
            if (!hasAssignedTasks) return false;
        }

        // Apply other filters
        switch (filterBy) {
            case 'priority':
                const releaseTasks = getReleaseTasks(release._id);
                const hasHighPriorityTasks = releaseTasks.some(task => 
                    task.priority === 'urgent' || task.priority === 'high'
                );
                return hasHighPriorityTasks;
            default:
                return true;
        }
    });

    // Calculate day width in pixels (for fixed width timeline)
    const DAY_WIDTH = 30; // pixels per day
    const totalDays = differenceInDays(timelineBounds.end, timelineBounds.start) + 1;
    const timelineWidth = totalDays * DAY_WIDTH;

    return (
        <div className="space-y-6 relative">
            {/* Beta Overlay */}
            {showBetaOverlay && (
                <div className="absolute inset-0 bg-background/20 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-background border border-muted rounded-lg p-6 shadow-lg max-w-lg mx-4">
                        <h3 className="text-lg font-semibold mb-2">Release View (Beta)</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            This is a new beta feature for visualizing your release timeline. 
                            We're actively improving it based on your feedback.
                        </p>
                        <Button onClick={handleDismissBeta} className="w-full">
                            Got it!
                        </Button>
                    </div>
                </div>
            )}
            {filteredReleases.length === 0 ? (
                <Card>
                    <CardContent className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <FiPackage className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-medium mb-2">No releases found</h3>
                            <p className="text-sm text-muted-foreground">
                                {filterByAssignee.size > 0 || filterBy !== 'none' 
                                    ? 'Try adjusting your filters' 
                                    : 'Create your first release to get started'
                                }
                            </p>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card className="overflow-hidden">
                    <CardContent className="p-0">
                        <div className="flex">
                            {/* Fixed Release Column */}
                            <div className="flex-shrink-0 border-r border-muted">
                                {/* Header */}
                                <div className="h-12 px-4 flex items-center border-b border-muted">
                                    <span className="text-xs font-medium text-muted-foreground">Releases</span>
                                </div>
                                
                                {/* Release Rows */}
                                <div>
                                    {filteredReleases.map((release) => {
                                        const colors = getReleaseStatusColor(release.status);
                                        const tasks = getReleaseTasks(release._id);
                                        const progress = getReleaseProgress(release._id);
                                        const startDate = release.startDate ? new Date(release.startDate) : null;
                                        const endDate = release.targetDate ? new Date(release.targetDate) : null;
                                        const actualReleaseDate = release.actualReleaseDate ? new Date(release.actualReleaseDate) : null;
                                        
                                        return (
                                            <div 
                                                key={release._id} 
                                                className={cn(
                                                    "h-16 px-4 flex items-center border-b border-muted/50 transition-colors cursor-pointer group",
                                                    hoveredReleaseId === release._id ? "bg-muted/20" : "hover:bg-muted/20"
                                                )}
                                                onClick={() => onShowReleaseDetails && onShowReleaseDetails(release._id)}
                                                onMouseEnter={() => setHoveredReleaseId(release._id)}
                                                onMouseLeave={() => setHoveredReleaseId(null)}
                                                data-release-id={release._id}
                                            >
                                                <div className="flex items-center gap-3 w-80">
                                                    <FiPackage className={cn("w-4 h-4 flex-shrink-0", colors.icon)} />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="font-medium text-sm truncate">{release.name}</h3>
                                                            <Badge 
                                                                variant="secondary" 
                                                                className={cn(colors.bg, colors.text, colors.border, "text-[10px] px-1.5 py-0.5 font-medium")}
                                                            >
                                                                {release.status.charAt(0).toUpperCase() + release.status.slice(1)}
                                                            </Badge>
                                                            {progress > 0 && (
                                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                                                                    {progress}%
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                                            {startDate && (
                                                                <div className="flex items-center gap-1">
                                                                    <FiCalendar className="w-3 h-3" />
                                                                    <span>{format(startDate, 'MMM d')}</span>
                                                                </div>
                                                            )}
                                                            {endDate && startDate && (
                                                                <span>→</span>
                                                            )}
                                                            {actualReleaseDate ? (
                                                                <span className="text-emerald-600 dark:text-emerald-500 font-medium">
                                                                    {format(actualReleaseDate, 'MMM d')}
                                                                </span>
                                                            ) : endDate ? (
                                                                <span>{format(endDate, 'MMM d')}</span>
                                                            ) : null}
                                                            <span>•</span>
                                                            <span>{tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            
                            {/* Scrollable Timeline */}
                            <div ref={timelineRef} className="flex-1 overflow-x-auto">
                                <div style={{ width: `${timelineWidth}px` }}>
                                    {/* Timeline Header */}
                                    <div className="h-12 border-b border-muted relative">
                                        {timelineDays.map((day, index) => {
                                            const isWeekStart = day.getDay() === 0 || index === 0;
                                            const isMonthStart = day.getDate() === 1;
                                            const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                                            
                                            return (
                                                <div
                                                    key={index}
                                                    className="absolute top-0 h-full flex items-center justify-center border-r border-muted/30"
                                                    style={{
                                                        left: `${index * DAY_WIDTH}px`,
                                                        width: `${DAY_WIDTH}px`
                                                    }}
                                                >
                                                    <span className={cn(
                                                        "text-[10px]",
                                                        isToday ? "text-foreground font-medium" : 
                                                        isMonthStart ? "text-muted-foreground font-semibold" :
                                                        "text-muted-foreground/50",
                                                        isMonthStart ? "transform -rotate-90 origin-center uppercase whitespace-nowrap" : ""
                                                    )}>
                                                        {isMonthStart ? format(day, 'MMM d') : format(day, 'd')}
                                                    </span>

                                                </div>
                                            );
                                        })}
                                        
                                    </div>
                                    
                                    {/* Release Timeline Bars */}
                                    <div className="relative">
                                        {/* Today's vertical line spanning entire chart */}
                                        <div 
                                            className="absolute top-0 bottom-0 w-0.5 bg-foreground z-10 pointer-events-none"
                                            style={{
                                                left: `${(differenceInDays(new Date(), timelineBounds.start) * DAY_WIDTH) + (DAY_WIDTH / 2)}px`
                                            }}
                                        />
                                        
                                        {/* Active Week Highlight */}
                                        <div 
                                            className="absolute top-0 bottom-0 bg-emerald-50 dark:bg-emerald-950/30 z-5 pointer-events-none"
                                            style={{
                                                left: `${differenceInDays(currentWeekBounds.start, timelineBounds.start) * DAY_WIDTH}px`,
                                                width: `${7 * DAY_WIDTH}px`
                                            }}
                                        />
                                        
                                        {filteredReleases.map((release) => {
                                            const colors = getReleaseStatusColor(release.status);
                                            const startDate = release.startDate ? new Date(release.startDate) : null;
                                            const endDate = release.targetDate ? new Date(release.targetDate) : null;
                                            
                                            const actualReleaseDate = release.actualReleaseDate ? new Date(release.actualReleaseDate) : null;
                                            
                                            const getTimelinePosition = () => {
                                                // Use actual release date if available, otherwise target date
                                                const effectiveEndDate = actualReleaseDate || endDate;
                                                
                                                // If we have an end date but no start date
                                                if (!startDate && effectiveEndDate) {
                                                    const now = new Date();
                                                    
                                                    // If the release is shipped or target date has passed
                                                    if (actualReleaseDate || isAfter(now, effectiveEndDate)) {
                                                        const targetOffset = differenceInDays(effectiveEndDate, timelineBounds.start);
                                                        return { 
                                                            left: targetOffset * DAY_WIDTH,
                                                            width: DAY_WIDTH, // Single day width
                                                            hasStartDate: false,
                                                            hasTargetDate: true,
                                                            isShipped: !!actualReleaseDate
                                                        };
                                                    }
                                                    
                                                    // If target date is in the future, show from current date to target
                                                    const currentOffset = differenceInDays(now, timelineBounds.start);
                                                    const duration = differenceInDays(effectiveEndDate, now) + 1;
                                                    
                                                    return { 
                                                        left: currentOffset * DAY_WIDTH,
                                                        width: duration * DAY_WIDTH,
                                                        hasStartDate: false,
                                                        hasTargetDate: true,
                                                        isShipped: false
                                                    };
                                                }
                                                
                                                // If we have a start date but no end date
                                                if (startDate && !endDate && !actualReleaseDate) {
                                                    const startOffset = differenceInDays(startDate, timelineBounds.start);
                                                    const now = new Date();
                                                    const currentOffset = differenceInDays(now, timelineBounds.start);
                                                    
                                                    // Show from start date to today
                                                    const duration = Math.max(1, currentOffset - startOffset + 1);
                                                    
                                                    return { 
                                                        left: startOffset * DAY_WIDTH,
                                                        width: duration * DAY_WIDTH,
                                                        hasStartDate: true,
                                                        hasTargetDate: false,
                                                        isShipped: false
                                                    };
                                                }
                                                
                                                // Normal case: both start and end dates
                                                if (startDate && effectiveEndDate) {
                                                    const startOffset = differenceInDays(startDate, timelineBounds.start);
                                                    const duration = differenceInDays(effectiveEndDate, startDate) + 1;
                                                    
                                                    return { 
                                                        left: startOffset * DAY_WIDTH,
                                                        width: duration * DAY_WIDTH,
                                                        hasStartDate: true,
                                                        hasTargetDate: true,
                                                        isShipped: !!actualReleaseDate
                                                    };
                                                }
                                                
                                                return { left: 0, width: 0, hasStartDate: false, hasTargetDate: false, isShipped: false };
                                            };
                                            
                                            // Calculate positions for actual release date marker
                                            const getActualReleaseDatePosition = () => {
                                                if (!actualReleaseDate) return null;
                                                const actualOffset = differenceInDays(actualReleaseDate, timelineBounds.start);
                                                return actualOffset * DAY_WIDTH;
                                            };

                                            const timelinePos = getTimelinePosition();

                                            return (
                                                <div 
                                                    key={release._id} 
                                                    className={cn(
                                                        "h-16 relative border-b border-muted/50 transition-colors cursor-pointer group",
                                                        hoveredReleaseId === release._id ? "bg-muted/20" : "hover:bg-muted/20"
                                                    )}
                                                    onClick={() => onShowReleaseDetails && onShowReleaseDetails(release._id)}
                                                    onMouseEnter={() => setHoveredReleaseId(release._id)}
                                                    onMouseLeave={() => setHoveredReleaseId(null)}
                                                    data-release-id={release._id}
                                                >
                                                    {/* Grid lines */}
                                                    {timelineDays.map((_, index) => (
                                                        <div
                                                            key={index}
                                                            className="absolute top-0 h-full border-r border-muted/30"
                                                            style={{
                                                                left: `${index * DAY_WIDTH}px`,
                                                                width: `${DAY_WIDTH}px`
                                                            }}
                                                        />
                                                    ))}
                                                    
                                                    {/* Timeline Bar */}
                                                    {(endDate || actualReleaseDate || startDate) && (
                                                        <div 
                                                            className={cn(
                                                                "absolute top-3 h-10 transition-all duration-200",
                                                                colors.bg,
                                                                colors.border,
                                                                timelinePos.hasStartDate && timelinePos.hasTargetDate ? "rounded-sm" : 
                                                                timelinePos.hasStartDate ? "rounded-l-sm" : "rounded-r-sm"
                                                            )}
                                                            style={{
                                                                left: `${timelinePos.left}px`,
                                                                width: `${Math.max(20, timelinePos.width)}px`
                                                            }}
                                                        >
                                                            {!timelinePos.hasStartDate && (
                                                                <>
                                                                    {/* Zig-zag left edge */}
                                                                    <div 
                                                                        className="absolute left-0 top-0 h-full w-2"
                                                                        style={{
                                                                            background: `repeating-linear-gradient(
                                                                                -45deg,
                                                                                transparent,
                                                                                transparent 4px,
                                                                                ${release.status === 'active' ? 'rgba(59, 130, 246, 0.6)' : 
                                                                                  release.status === 'upcoming' ? 'rgba(115, 115, 115, 0.6)' :
                                                                                  release.status === 'overdue' ? 'rgba(239, 68, 68, 0.6)' :
                                                                                  'rgba(16, 185, 129, 0.6)'} 4px,
                                                                                ${release.status === 'active' ? 'rgba(59, 130, 246, 0.6)' : 
                                                                                  release.status === 'upcoming' ? 'rgba(115, 115, 115, 0.6)' :
                                                                                  release.status === 'overdue' ? 'rgba(239, 68, 68, 0.6)' :
                                                                                  'rgba(16, 185, 129, 0.6)'} 8px
                                                                            )`
                                                                        }}
                                                                    />
                                                                    {/* Striped pattern overlay */}
                                                                    <div className="absolute inset-0 bg-white/10 dark:bg-black/10 rounded-r-sm" 
                                                                         style={{
                                                                             backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.05) 3px, rgba(255,255,255,0.05) 6px)'
                                                                         }} />
                                                                </>
                                                            )}
                                                            {!timelinePos.hasTargetDate && (
                                                                <>
                                                                    {/* Zig-zag right edge */}
                                                                    <div 
                                                                        className="absolute right-0 top-0 h-full w-2"
                                                                        style={{
                                                                            background: `repeating-linear-gradient(
                                                                                -45deg,
                                                                                transparent,
                                                                                transparent 4px,
                                                                                ${release.status === 'active' ? 'rgba(59, 130, 246, 0.6)' : 
                                                                                  release.status === 'upcoming' ? 'rgba(115, 115, 115, 0.6)' :
                                                                                  release.status === 'overdue' ? 'rgba(239, 68, 68, 0.6)' :
                                                                                  'rgba(16, 185, 129, 0.6)'} 4px,
                                                                                ${release.status === 'active' ? 'rgba(59, 130, 246, 0.6)' : 
                                                                                  release.status === 'upcoming' ? 'rgba(115, 115, 115, 0.6)' :
                                                                                  release.status === 'overdue' ? 'rgba(239, 68, 68, 0.6)' :
                                                                                  'rgba(16, 185, 129, 0.6)'} 8px
                                                                            )`
                                                                        }}
                                                                    />
                                                                    {/* Striped pattern overlay for right edge */}
                                                                    <div className="absolute inset-0 bg-white/10 dark:bg-black/10 rounded-l-sm" 
                                                                         style={{
                                                                             backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.05) 3px, rgba(255,255,255,0.05) 6px)'
                                                                         }} />
                                                                </>
                                                            )}
                                                            {/* Shipped indicator at the end */}
                                                            {timelinePos.isShipped && (
                                                                <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-background rounded-full flex items-center justify-center">
                                                                    <CheckCircle2 
                                                                        className="w-3 h-3 text-emerald-600 dark:text-emerald-500"
                                                                        title={`Shipped: ${format(actualReleaseDate, 'MMM d, yyyy')}`}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
        
    );
} 